import * as React from 'react';
import { clsx } from 'clsx';

export interface TooltipProps {
  /**
   * The content to display in the tooltip
   */
  content: string;
  /**
   * The trigger element that shows the tooltip on hover
   */
  children: React.ReactNode;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const tooltipStyles: React.CSSProperties = {
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#2f2f2f',
  color: '#ffffff',
  padding: '12px',
  borderRadius: '8px',
  boxShadow: '0px 4px 12px 0px rgba(32, 32, 32, 0.08)',
  fontSize: '13px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontWeight: 400,
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
  zIndex: 1000,
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
};

const wrapperStyles: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block',
};

/**
 * Tooltip component - Arbor Design System
 *
 * A tooltip that appears on hover over a trigger element.
 */
export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      content,
      children,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(false);

    const visibleTooltipStyles: React.CSSProperties = {
      ...tooltipStyles,
      opacity: isVisible ? 1 : 0,
      ...style,
    };

    return (
      <div
        ref={ref}
        style={wrapperStyles}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        data-testid={dataTestId}
      >
        {children}
        <div
          className={clsx('arbor-tooltip', className)}
          style={visibleTooltipStyles}
          role="tooltip"
          aria-hidden={!isVisible}
        >
          {content}
        </div>
      </div>
    );
  }
);

Tooltip.displayName = 'Tooltip';
